import './ListPage.scss';
import { Button, Icon, SimpleNumberCard, Checkbox } from '@design-system/index';
import { ArchiveLine, ArchiveFilled, AppartementsLine, AddDashed, UnarchiveLine, UnarchiveFilled, RoundedArrow, SyncroError } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ApartmentTable } from '@services/member/apartment/apartment.types';
import { ColumnDef } from '@tanstack/react-table';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { useListPageVM } from './useListPageVM';

export default function ListPage() {
    const { handleToggleArchiveApartment, handleToggleArchiveApartments, handleMetadata, navigate, columnHelper, isArchived, setIsArchived, totalApartments, mutationHook, isLoading, needReload } = useListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
                
            )
        }),
        columnHelper.accessor('reference', { 
            header: 'Référence',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</> 
        }),
        columnHelper.accessor('label', {
            header: 'Label',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</> 
        }),
        columnHelper.accessor('formattedCity', {
            header: 'Ville',
            id: 'address.city',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</> 
        }),

        columnHelper.accessor('numberOfBeds', {
            header: 'Nb de couchages',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),

        columnHelper.accessor('publicLink', {
            header: 'Voir en ligne',
            meta: {
                clickable: false,
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {(isArchived || info.row.original?.syncroStatus === 'not_synced') ? (
                        <>-</>
                    ) : (
                        (info.row.original?.syncroStatus === 'error') ? (
                            <Icon icon={<SyncroError />} size={20} color="error" />
                        ) : (
                            <Button
                                variant="icon-hover"
                                color="grey-secondary"
                                icon={<Icon icon={<RoundedArrow />} size={24} />}
                                onClick={() => {
                                    window.open(info.getValue(), '_blank');
                                }}
                            />
                        )
                    )}
                </div>
            )
        }),

        columnHelper.display({
            id: 'archive',
            size: 0,
            header: isArchived ? 'Désarchiver' : 'Archiver',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (<div style={{display: 'flex', justifyContent: 'center'}}>
                <Button
                    variant="icon"
                    color="primary"
                    size="small"
                    hoverIcon={isArchived ? <Icon icon={<UnarchiveFilled />} size={20} color="primary" /> : <Icon icon={<ArchiveFilled />} size={20} color="primary" />}
                    icon={isArchived? <Icon icon={<UnarchiveLine />} size={20} color="primary" /> : <Icon icon={<ArchiveLine />} size={20} color="primary" />}
                    onClick={() => handleToggleArchiveApartment(info.row.original.id, !isArchived)}
                />
                </div>)
            }
        })
    ] as ColumnDef<ApartmentTable>[];

    return (        
        <div className="apartments-table-container">
            <div className="apartments-table-container-header">
                <div className="apartments-table-container-header-left">
                    <SimpleNumberCard labelForSingle={isArchived ? 'Logement archivé' : 'Logement'} labelForMultiple={isArchived ? 'Logements archivés' : 'Logements'} value={totalApartments} />
                    <LargeIconButton icon={<AddDashed />} label="Nouveau logement" onClick={() => navigate('/members-management/apartments/add')} />
                </div>
                <Button variant="secondary" onClick={() => setIsArchived(!isArchived)}>
                    {isArchived ? 'Actifs' : 'Archivés'}
                </Button>
            </div>
            <MutationTable
                key={isArchived ? 'archived' : 'active'}
                columns={columns}
                mutationHook={mutationHook}
                onRowClick={(row) => navigate(`/members-management/apartments/${row.id}/details`)}
                defaultSorting={{
                    field: 'id',
                    direction: 'desc'
                }}
                deleting={{
                    enabled: isArchived,
                    entity: 'apartment',
                }}
                headerTitle={isArchived ? 'Liste des logements archivés' : 'Liste des logements'}
                headerIcon={<AppartementsLine />}
                onMetadata={handleMetadata}
                forceLoading={isLoading}
                needReload={needReload}
                filters={[
                    {
                        id: "isAvailable",
                        label: "Date de disponibilité",
                        type: "date",
                        comparatorTypes: ['isBetween']
                    }
                ]}
                footer={ 
                    {
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : "Archiver",
                                icon: isArchived? <Icon icon={<UnarchiveLine />} size={20} color="primary" /> : <Icon icon={<ArchiveLine />} size={20} color="primary" />,
                                hoverIcon: isArchived ? <Icon icon={<UnarchiveFilled />} size={20} color="primary" /> : <Icon icon={<ArchiveFilled />} size={20} color="primary" />,
                                onClick: (lines: ApartmentTable[]) => handleToggleArchiveApartments(lines, !isArchived)
                            }
                        ]
                    }
                }
            />
        </div>
    );
};
